import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/context/LanguageContext";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language, t } = useLanguage();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);

    const hashType = hashParams.get("type");
    const hashAccessToken = hashParams.get("access_token");
    const queryType = queryParams.get("type");
    const tokenHash = queryParams.get("token_hash");

    const hasRecoveryError =
      !!hashParams.get("error_code") ||
      !!hashParams.get("error_description") ||
      !!queryParams.get("error_code") ||
      !!queryParams.get("error_description");

    const isHashRecovery = hashType === "recovery" && !!hashAccessToken;
    const isTokenHashRecovery = queryType === "recovery" && !!tokenHash;

    if (hasRecoveryError || (!isHashRecovery && !isTokenHashRecovery)) {
      setIsValidSession(false);
      setIsChecking(false);
      return;
    }

    const checkSession = async () => {
      if (isTokenHashRecovery && tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          type: "recovery",
          token_hash: tokenHash,
        });

        if (error) {
          setIsValidSession(false);
          setIsChecking(false);
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      setIsValidSession(!!session);
      setIsChecking(false);
    };

    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: t.resetPassword.error, description: t.resetPassword.mismatch, variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: t.resetPassword.error, description: t.resetPassword.tooShort, variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast({ title: t.resetPassword.error, description: error.message, variant: "destructive" });
      setIsSubmitting(false);
      return;
    }

    setIsSuccess(true);
    toast({ title: t.resetPassword.successTitle, description: t.resetPassword.successDesc });
    // Sign out the recovery session, then redirect to login
    await supabase.auth.signOut();
    setTimeout(() => navigate("/login"), 2000);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const iconClass = "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground rtl:left-auto rtl:right-3";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 pb-20 px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-serif mb-2">{t.resetPassword.title}</h1>
            <p className="text-muted-foreground">{t.resetPassword.subtitle}</p>
          </div>

          <div className="bg-card border border-border p-8 rounded-sm">
            {isSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-serif mb-2">{t.resetPassword.successTitle}</h3>
                <p className="text-muted-foreground">{t.resetPassword.redirecting}</p>
              </div>
            ) : !isValidSession ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-6">{t.resetPassword.invalidLink}</p>
                <div className="flex flex-col gap-3">
                  <Button variant="elegant" onClick={() => navigate("/forgot-password")}>
                    {language === "en" ? "Request New Reset Link" : "طلب رابط إعادة تعيين جديد"}
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/login")}>
                    {t.resetPassword.backToLogin}
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className={iconClass} />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t.resetPassword.newPassword}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 rtl:pl-10 rtl:pr-10"
                    required
                    disabled={isSubmitting}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground rtl:right-auto rtl:left-3">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className={iconClass} />
                  <Input
                    type={showConfirm ? "text" : "password"}
                    placeholder={t.resetPassword.confirmPassword}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 rtl:pl-10 rtl:pr-10"
                    required
                    disabled={isSubmitting}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground rtl:right-auto rtl:left-3">
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button type="submit" variant="elegant" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.resetPassword.updating}</>) : t.resetPassword.updateBtn}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPassword;
